<template>
  <div>
    <tabs @change="onTabChange">
      <tab title="Existing Folder" v-if="allStudents && allStudents.length > 0">
        <br>
        <div class="form-group">
          <input
            type="text"
            class="form-control"
            placeholder="Filter folders"
            v-model="studentNameFilter"
            v-if="allStudents.length > 5">
        </div>
        <h5>Recent folders</h5>
        <ul class="nav nav-pills">
          <li
            v-for="student in filteredRecentStudents"
            :key="student.uuid"
            :class="{active: isSelectedExistingStudent(student.name)}">
            <button
              :class="['fs-block', 'link-button', (isSelectedExistingStudent(student.name)) ? 'selected-student' : 'unselected-student']"
              :aria-pressed="isSelectedExistingStudent(student.name) ? 'true' : 'false'"
              @click="selectStudent(student)">{{ student.name }}</button>
          </li>
        </ul>

        <template v-if="filteredStudentsMinusRecent.length > 0">
          <h5>Other folders</h5>
          <ul class="nav nav-pills">
            <li
              v-for="student in filteredStudentsMinusRecent"
              :key="student.uuid"
              :class="{active: isSelectedExistingStudent(student.name)}">
              <button
                :class="['fs-block', 'link-button', (isSelectedExistingStudent(student.name)) ? 'selected-student' : 'unselected-student']"
                :aria-pressed="isSelectedExistingStudent(student.name) ? 'true' : 'false'"
                @click="selectStudent(student)">{{ student.name }}
              </button>
            </li>
          </ul>
        </template>
      </tab>
      <tab title="New Folder">
        <h5>Enter folder name <StudentPiiTooltip/></h5>
        <div class="form-group student-name" :class="{'has-error': !isNewStudent && newStudentName}">
          <input
            :disabled="maxStudentsReached"
            v-model="newStudentName"
            type="text"
            class="form-control fs-block"
            placeholder="Folder name"
            @keyup="newStudentNameUpdated">
          <span v-show="isNewStudent" class="label label-info">New folder</span>
          <span v-show="!isNewStudent && newStudentName" class="label label-danger">Folder already exists</span>
        </div>
        <alert v-if="maxStudentsReached" type="warning">
          <b>Maximum folders reached.</b> {{ maxStudentsErrorMessage }}
        </alert>
      </tab>
    </tabs>

  </div>
</template>

<script>
import { Tabs, Tab } from 'uiv'
import StudentPiiTooltip from '@/components/library/StudentPiiTooltip'
import { libraryQuotasMixin } from '@/components/library/library_quotas_mixin'
import { escapeRegExp } from '@/util/regex'

export default {
  name: 'ChooseStudentForm',
  mixins: [libraryQuotasMixin],
  components: {
    Tabs,
    Tab,
    StudentPiiTooltip
  },
  data() {
    return {
      studentNameFilter: '',
      newStudentName: '',
      selectedExistingStudent: null
    }
  },
  props: {
    allStudents: {
      type: Array,
      default() {
        return []
      }
    },
    recentStudents: {
      type: Array,
      default() {
        return []
      }
    },
    preselectedStudent: {
      type: Object,
      required: false,
      default() {
        return null
      }
    }
  },
  computed: {
    activeStudent() {
      if (!this.newStudentName) {
        return this.selectedExistingStudent
      } else {
        return { name: this.newStudentName }
      }
    },
    allStudentNames() {
      return this.allStudents.map(student => student.name)
    },
    isNewStudent() {
      return this.newStudentName && this.allStudentNames.indexOf(this.newStudentName) < 0
    },
    studentNameRegex() {
      return (this.studentNameFilter && new RegExp(escapeRegExp(this.studentNameFilter), 'i')) || null
    },
    filteredRecentStudents() {
      if (this.studentNameRegex) {
        return this.recentStudents.filter(student => {
          return student.name.match(this.studentNameRegex)
        })
      } else {
        return this.recentStudents
      }
    },
    filteredStudentsMinusRecent() {
      return this.allStudents.filter(student => {
        let name = student.name
        let notInRecent = this.recentStudents.every(student => {
          return student.name !== name
        })
        if (this.studentNameRegex) {
          return notInRecent && name.match(this.studentNameRegex)
        } else {
          return notInRecent
        }
      })
    }
  },
  methods: {
    selectStudent(student) {
      this.selectedExistingStudent = { name: student.name, uuid: student.uuid }
      this.newStudentName = ''
      this.$emit('selectedStudent:existing', this.activeStudent)
    },
    newStudentNameUpdated() {
      if (this.newStudentName && this.selectedExistingStudent) {
        this.selectedExistingStudent = null
      }
      if (this.isNewStudent) {
        this.$emit('selectedStudent:new', this.activeStudent)
      } else {
        this.$emit('selectedStudent:new', null)
      }
    },
    onTabChange(index) {
      if (index === 1) {
        if (this.maxStudentsReached) {
          this.sendMaxStudentsReachedEvent()
        }
      }
    },
    isSelectedExistingStudent(studentName) {
      return this.selectedExistingStudent && (this.selectedExistingStudent.name === studentName)
    }
  },
  created() {
    if (this.preselectedStudent) {
      this.selectedExistingStudent = this.preselectedStudent
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/variables.less';
h5 {
  margin-top: 16px;
}
.student-name {
  position: relative;
  .label {
    position: absolute;
    top: 50%;
    right: 8px;
    margin-top: -7px;
  }
}
.selected-student, .unselected-student {
  padding: 10px 15px;
  border-radius: 4px;
  &:hover {
    color: @link-hover-color;
    text-decoration: none;
  }
}

.unselected-student:hover {
  background: #EEEEEE;
}

.nav-pills {
  margin-bottom: 16px;
  li {
    border: 1px solid transparent;
  }
  li.active {
    button,
    button:hover {
      border: 1px solid @link-color;
      color: @link-color;
      font-weight: bold;
    }
  }
}
</style>
